import { useEffect } from "react";
import { useReactFlow, useStoreApi } from "@xyflow/react";

/**
 * Pinch-to-zoom over canvas CONTENT (PB-5441 BUG-14; design owner approved,
 * decision 17). Data-blind: it knows DOM regions, never a module.
 *
 * React Flow zooms only for wheel events that reach its zoom pane. Two kinds of
 * content never get there with a pinch:
 *  - nodes whose body is marked `.nowheel` (so their lists scroll): React Flow
 *    prevents the page zoom there but deliberately does not zoom the canvas;
 *  - the widget, note and background layers, which are DOM siblings of
 *    `.react-flow`, so their events never reach its listener at all — the
 *    browser zoomed the whole page instead.
 *
 * One native, non-passive listener on the canvas root handles exactly those two
 * cases: a ctrl-wheel (a trackpad pinch arrives as one) is turned into a
 * pointer-anchored zoom with React Flow's own pinch factor, clamped. A plain wheel
 * is never touched, so a card list still scrolls; a target inside React Flow's own
 * pane is left to React Flow, so nothing zooms twice; the floating controls (not
 * content) are unchanged; and a widget that zooms itself opts out with
 * `data-canvas-own-wheel`. macOS Safari reports a pinch as `gesture*` events, which
 * are handled the same way.
 */
export interface CanvasPinchForwarderProps {
  /** The canvas root: the listener's host. */
  readonly root: HTMLElement | null;
  /** The overlay content host (widgets, notes, background). */
  readonly contentHost: HTMLElement | null;
  readonly minZoom: number;
  readonly maxZoom: number;
}

const OWN_WHEEL = "[data-canvas-own-wheel]";

/** React Flow's wheel-to-zoom factor (`@xyflow/system` `wheelDelta`). */
function pinchDelta(event: WheelEvent): number {
  const isMac =
    typeof navigator !== "undefined" &&
    /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);
  const factor = event.ctrlKey && isMac ? 10 : 1;
  const unit = event.deltaMode === 1 ? 0.05 : event.deltaMode ? 1 : 0.002;
  return -event.deltaY * unit * factor;
}

/**
 * Whether a pinch at `target` is ours to forward: inside a `.nowheel` node body or
 * inside the content host outside React Flow's pane, and not opted out.
 */
export function isForwardablePinchTarget(
  target: EventTarget | null,
  contentHost: HTMLElement | null,
): boolean {
  if (!(target instanceof Element)) return false;
  if (target.closest(OWN_WHEEL)) return false;
  if (target.closest(".nowheel")) return true;
  if (target.closest(".react-flow__renderer")) return false;
  return contentHost !== null && contentHost.contains(target);
}

export function CanvasPinchForwarder({
  root,
  contentHost,
  minZoom,
  maxZoom,
}: CanvasPinchForwarderProps): null {
  const { getViewport, setViewport } = useReactFlow();
  const store = useStoreApi();

  useEffect(() => {
    if (!root) return;
    const zoomAround = (
      clientX: number,
      clientY: number,
      nextZoomOf: (zoom: number) => number,
    ): void => {
      const pane = store.getState().domNode ?? root;
      const rect = pane.getBoundingClientRect();
      const { x, y, zoom } = getViewport();
      const next = Math.min(maxZoom, Math.max(minZoom, nextZoomOf(zoom)));
      if (next === zoom) return;
      const px = clientX - rect.left;
      const py = clientY - rect.top;
      const scale = next / zoom;
      void setViewport({
        x: px - (px - x) * scale,
        y: py - (py - y) * scale,
        zoom: next,
      });
    };

    const onWheel = (event: WheelEvent): void => {
      if (!event.ctrlKey) return;
      if (!isForwardablePinchTarget(event.target, contentHost)) return;
      event.preventDefault();
      const delta = pinchDelta(event);
      zoomAround(event.clientX, event.clientY, (zoom) => zoom * 2 ** delta);
    };

    // Safari: a pinch is gesturestart → gesturechange(scale) → gestureend.
    let gestureStartZoom: number | null = null;
    type GestureLike = Event & {
      readonly scale?: number;
      readonly clientX?: number;
      readonly clientY?: number;
    };
    const onGestureStart = (event: Event): void => {
      if (!isForwardablePinchTarget(event.target, contentHost)) return;
      event.preventDefault();
      gestureStartZoom = getViewport().zoom;
    };
    const onGestureChange = (event: Event): void => {
      if (gestureStartZoom === null) return;
      event.preventDefault();
      const gesture = event as GestureLike;
      // The fallback centre is the pane's, the box `zoomAround` measures
      // against, so an offset between the pane and `root` cannot move it
      // (CodeRabbit round 4).
      const rect = (store.getState().domNode ?? root).getBoundingClientRect();
      const startZoom = gestureStartZoom;
      zoomAround(
        gesture.clientX ?? rect.left + rect.width / 2,
        gesture.clientY ?? rect.top + rect.height / 2,
        () => startZoom * (gesture.scale ?? 1),
      );
    };
    const onGestureEnd = (): void => {
      gestureStartZoom = null;
    };

    root.addEventListener("wheel", onWheel, { passive: false });
    root.addEventListener("gesturestart", onGestureStart);
    root.addEventListener("gesturechange", onGestureChange);
    root.addEventListener("gestureend", onGestureEnd);
    return () => {
      root.removeEventListener("wheel", onWheel);
      root.removeEventListener("gesturestart", onGestureStart);
      root.removeEventListener("gesturechange", onGestureChange);
      root.removeEventListener("gestureend", onGestureEnd);
    };
  }, [contentHost, getViewport, maxZoom, minZoom, root, setViewport, store]);

  return null;
}
